import { useCallback, useState } from "react";
import classNames from "classnames";
import { Position, Spinner, Text } from "@blueprintjs/core";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useHistory } from "react-router";

import { DropdownContent } from "src/containers/conversations/components/dropdown-content/dropdown-content";
import { replaceToQueue, returnToQueue } from "src/actions/conversations/conversations-api";
import { ConversationQueue } from "src/store";
import { MPDIcon, MPDSelect, MPDSelectItem } from "src/mpd-library";
import { PageLoadingSpinner } from "src/components/page-loading-spinner/page-loading-spinner";
import { EmptyBlock, StyledButton, StyledButtonTypes } from "src/components";
import { getSelectedConversationAndId } from "src/selectors";
import useInfiniteScroll from "../../../../useInfiniteScroll";
import styles from "./styles.module.scss";
import { DropdownSearchHeader } from "src/components/dropdown-search-header/dropdown-search-header";
import { EmptyBlockTypes } from "../../../../../../../../components";
import { ReactComponent as DropdownSearchNoResultsIcon } from "src/mpd-library/icon/assets/dropdownSearchNoResults.svg";
import { ReactComponent as InboxIcon } from "src/mpd-library/icon/assets/inbox-icon.svg";

const classname = "queue-dropdown";

const lastQueueSelector = createSelector(
    getSelectedConversationAndId,
    ({ selectedConversation }) => selectedConversation?.queue
);

type QueuesDropdownProps = {
    showLeftIcon: boolean;
    place?: string;
    showHeader?: boolean;
    dropdownToChangeQueue?: boolean;
    notShowSubtitles?: boolean;
    notShowItemLeftIcon?: boolean;
    title: string;
    queuesProps: any;
};

export const QueuesDropdown = ({
    place,
    showLeftIcon = true,
    showHeader,
    dropdownToChangeQueue,
    notShowSubtitles,
    notShowItemLeftIcon,
    queuesProps
}: QueuesDropdownProps) => {
    const dispatch = useDispatch();
    const lastQueue = useSelector(lastQueueSelector);
    const isTabletSize = window.innerWidth < 1100;
    const { selectedConversation, selectedConversationId } = useSelector(getSelectedConversationAndId);

    const [returnToQueueProcessign, setReturnToQueueProcessing] = useState(false);
    const [isMoveToDropdownOpened, setIsMoveToDropdownOpened] = useState(false);

    const {
        queues,
        searchCount,
        counts: allQueuesLength,
        hasMore: hasMoreQueues,
        getQueuesProcessing,
        onGetQueues,
        setQueuesSearchValue: setSearchValue,
        searchValue
    } = queuesProps;

    const history = useHistory();

    const [bottomSentryRef] = useInfiniteScroll({
        loading: getQueuesProcessing,
        hasNextPage: hasMoreQueues,
        onLoadMore: useCallback(async () => await onGetQueues(true), [])
    });

    const returnToSelectedQueue = async (queueId?: string) => {
        try {
            setReturnToQueueProcessing(true);
            await dispatch(returnToQueue(queueId));
            history.replace(`/conversations`);
        } finally {
            setReturnToQueueProcessing(false);
        }
    };

    const onSearchChangeHandler = (event) => {
        const { value } = event.target;
        setSearchValue(value);
    };

    const replaceConversationToQueue = async (queueId: string) => {
        try {
            setReturnToQueueProcessing(true);
            const isAdminBelognsToNewQueue = await dispatch(replaceToQueue(queueId, selectedConversationId));
            if (!isAdminBelognsToNewQueue) {
                history.replace(`/conversations`);
            }
        } finally {
            setReturnToQueueProcessing(false);
        }
    };

    const onClickItemQueue = (queueId?: string) => {
        if (dropdownToChangeQueue) {
            replaceConversationToQueue(queueId);
        } else {
            returnToSelectedQueue(queueId);
        }

        setIsMoveToDropdownOpened(false);
    };

    return (
        <>
            {allQueuesLength > 1 && (
                <MPDSelect
                    ArrowIconComponent
                    isOpenFromParent={isMoveToDropdownOpened}
                    onToggleOptions={setIsMoveToDropdownOpened}
                    className={classNames("rounded-dropdown", styles[`${classname}-select`])}
                    defaultValue={(place && selectedConversation?.queue?.name) || "Return"}
                    value={(place && selectedConversation?.queue?.name) || "Return"}
                    leftIcon={showLeftIcon ? "returnIcon" : null}
                    position={Position.BOTTOM_LEFT}
                    usePortal
                    portalClassName={styles[`${classname}-queue-dropdown-portal`]}
                    customOptions={
                        <DropdownContent
                            header={
                                showHeader ? (
                                    <DropdownSearchHeader
                                        title={"Move To"}
                                        onSearchChangeHandler={onSearchChangeHandler}
                                        searchValue={searchValue}
                                        onClose={setIsMoveToDropdownOpened}
                                    />
                                ) : null
                            }
                            content={
                                <>
                                    {!returnToQueueProcessign && (
                                        <>
                                            {searchCount === 0 && searchValue && !getQueuesProcessing ? (
                                                <EmptyBlock
                                                    title={`No results for ‘${searchValue}’`}
                                                    IconComponent={DropdownSearchNoResultsIcon}
                                                    type={EmptyBlockTypes.dropdown}
                                                />
                                            ) : (
                                                <>
                                                    {!dropdownToChangeQueue && (
                                                        <div className={styles[`${classname}-queues-group`]}>
                                                            <Text className={styles[`${classname}-subtitle`]}>
                                                                CURRENT QUEUE
                                                            </Text>
                                                            <MPDSelectItem
                                                                className={styles[`${classname}-item`]}
                                                                onClick={() => {
                                                                    returnToSelectedQueue(lastQueue.id);
                                                                }}
                                                            >
                                                                <div className={styles[`${classname}-item-content`]}>
                                                                    {!notShowItemLeftIcon && (
                                                                        <MPDIcon
                                                                            IconComponent={InboxIcon}
                                                                            width={16}
                                                                            height={16}
                                                                            className={
                                                                                styles[`${classname}-inbox-icon`]
                                                                            }
                                                                        />
                                                                    )}
                                                                    <Text className={styles[`${classname}-tag-label`]}>
                                                                        {lastQueue?.name}
                                                                    </Text>
                                                                </div>
                                                            </MPDSelectItem>
                                                        </div>
                                                    )}
                                                    <div className={styles[`${classname}-queues-group`]}>
                                                        {!notShowSubtitles && (
                                                            <Text className={styles[`${classname}-subtitle`]}>
                                                                MOVE TO NEW QUEUE
                                                            </Text>
                                                        )}

                                                        <div className={styles[`${classname}-queues-wrapper`]}>
                                                            {queues.map((queue: ConversationQueue) => {
                                                                return (
                                                                    <MPDSelectItem
                                                                        className={styles[`${classname}-item`]}
                                                                        onClick={() => {
                                                                            onClickItemQueue(queue?.id);
                                                                        }}
                                                                        key={queue.id}
                                                                        selected={
                                                                            place &&
                                                                            selectedConversation?.queue?.id === queue.id
                                                                        }
                                                                    >
                                                                        <div
                                                                            className={
                                                                                styles[`${classname}-item-content`]
                                                                            }
                                                                        >
                                                                            {!notShowItemLeftIcon && (
                                                                                <MPDIcon
                                                                                    IconComponent={InboxIcon}
                                                                                    width={16}
                                                                                    height={16}
                                                                                    className={
                                                                                        styles[
                                                                                            `${classname}-inbox-icon`
                                                                                        ]
                                                                                    }
                                                                                />
                                                                            )}
                                                                            <Text
                                                                                className={
                                                                                    styles[`${classname}-tag-label`]
                                                                                }
                                                                            >
                                                                                {queue.name}
                                                                            </Text>
                                                                        </div>
                                                                    </MPDSelectItem>
                                                                );
                                                            })}
                                                            {(returnToQueueProcessign || getQueuesProcessing) &&
                                                                !queues.length && <PageLoadingSpinner />}
                                                            {(!!getQueuesProcessing || hasMoreQueues) &&
                                                                !!queues.length && (
                                                                    <div
                                                                        ref={bottomSentryRef}
                                                                        className={
                                                                            styles[
                                                                                `${classname}-load-more-spinner-wrapper`
                                                                            ]
                                                                        }
                                                                    >
                                                                        <Spinner intent="primary" size={20} />
                                                                    </div>
                                                                )}
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            }
                        />
                    }
                    minimized={isTabletSize && !place}
                />
            )}
            {allQueuesLength === 1 && place !== "opened-conversation" && (
                <StyledButton
                    type={StyledButtonTypes.secondary}
                    className={classNames(
                        "rounded-dropdown",
                        styles[`${classname}-dropdown`],
                        styles[`${classname}-return-btn`]
                    )}
                    text="Return"
                    name={"returnIcon"}
                    onClick={() => {
                        returnToSelectedQueue(lastQueue.id);
                    }}
                />
            )}
        </>
    );
};
